import { useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowUpRight } from "lucide-react";

const LINKS = [
  { to: "/", label: "Accueil", testId: "navbar-nav-home" },
  { to: "/services", label: "Services", testId: "navbar-nav-services" },
  { to: "/realisations", label: "Réalisations", testId: "navbar-nav-realisations" },
  { to: "/contact", label: "Contact", testId: "navbar-nav-contact" },
];

export const Wordmark = ({ light = true }) => (
  <span className="flex flex-col leading-none">
    <span className={`font-display text-xl font-extrabold tracking-tight ${light ? "text-white" : "text-roch-navy"}`}>
      LE&nbsp;ROCH
    </span>
    <span className="mt-1 font-mono text-[9px] uppercase tracking-[0.28em] text-roch-orange">
      couverture · étanchéité
    </span>
  </span>
);

export const Navbar = () => {
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();

  return (
    <header className="fixed inset-x-0 top-0 z-50">
      <div className="border-b border-white/10 bg-roch-navy/90 backdrop-blur-xl">
        <div className="mx-auto flex h-[72px] max-w-7xl items-center justify-between px-4 sm:px-6 lg:px-8">
          <Link to="/" data-testid="navbar-logo-link" className="shrink-0" onClick={() => setOpen(false)}>
            <Wordmark />
          </Link>

          <nav className="hidden items-center gap-8 md:flex">
            {LINKS.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                data-testid={l.testId}
                className={({ isActive }) =>
                  `group relative font-body text-sm font-medium transition-colors duration-300 ${
                    isActive ? "text-white" : "text-white/55 hover:text-white"
                  }`
                }
              >
                {({ isActive }) => (
                  <>
                    {l.label}
                    <span
                      className={`absolute -bottom-2 left-0 h-[2px] bg-roch-orange transition-all duration-300 ${
                        isActive ? "w-full" : "w-0 group-hover:w-full"
                      }`}
                    />
                  </>
                )}
              </NavLink>
            ))}
            <Link
              to="/devis"
              data-testid="navbar-nav-devis"
              className="group flex items-center gap-1.5 rounded-full bg-roch-orange px-5 py-2.5 text-sm font-semibold text-roch-navy transition-colors duration-300 hover:bg-roch-orangeHover"
            >
              Devis gratuit
              <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </Link>
          </nav>

          <button
            data-testid="navbar-mobile-menu-button"
            onClick={() => setOpen(!open)}
            className="flex h-10 w-10 items-center justify-center rounded-full border border-white/15 text-white md:hidden"
            aria-label="Menu"
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            data-testid="navbar-mobile-drawer"
            initial={{ opacity: 0, y: -12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            transition={{ duration: 0.25 }}
            className="border-b border-white/10 bg-roch-navy/[0.98] px-6 py-6 backdrop-blur-xl md:hidden"
          >
            <div className="flex flex-col gap-1">
              {[...LINKS, { to: "/devis", label: "Devis gratuit", testId: "navbar-nav-devis-mobile" }].map((l) => (
                <Link
                  key={l.to + l.testId}
                  to={l.to}
                  data-testid={l.testId}
                  onClick={() => setOpen(false)}
                  className={`rounded-xl px-4 py-3.5 font-display text-lg font-semibold transition-colors ${
                    pathname === l.to ? "bg-white/10 text-roch-orange" : "text-white/80 hover:bg-white/5"
                  }`}
                >
                  {l.label}
                </Link>
              ))}
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
};
